import {
  Box,
  Button,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { ColorMode } from 'theme';
import { useRecoilValue } from 'recoil';
import { isSideDrawerExpandedState, userDetailsState } from 'states';
import { HideSupportStyle } from '../HideSupportStyle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import useIsMobile from 'hooks/useIsMobile';

interface UpsellProps {
  title: string;
  subtitle: string;
  items: string[];
  imageUrl?: string;
}

export const Upsell = ({ title, subtitle, items, imageUrl }: UpsellProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const userDetails = useRecoilValue(userDetailsState);
  const isMobile = useIsMobile();
  const sidebarExpanded = useRecoilValue(isSideDrawerExpandedState);

  const isPortrait = window.innerHeight > window.innerWidth;
  const backgroundImageUrl = `images/upsell/${
    theme.colorMode === ColorMode.LIGHT ? 'light' : 'dark'
  }-${isPortrait ? 'portrait' : 'landscape'}-${imageUrl}.png`;

  const sidebarOffset = sidebarExpanded ? 240 : 60;
  return (
    <Box
      sx={{
        backgroundImage: isMobile ? undefined : `url("${backgroundImageUrl}")`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        width: `calc(100% - ${sidebarOffset}px)`,
        maxWidth: '100%',
        height: '100%',
        position: 'fixed',
        left: isMobile ? '0px' : `${sidebarOffset}px`,
        top: isMobile ? '65px' : '0px',
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: isMobile ? undefined : 'rgba(0,0,0,0.4)',
          zIndex: 1,
        }}
      />
      <HideSupportStyle />
      <Paper
        elevation={8}
        sx={{
          padding: '2em',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          textAlign: 'center',
          gap: '12px',
          zIndex: 2,
        }}
      >
        <Typography
          sx={{
            color: theme.palette.text.disabled,
            fontSize: '16px',
            textTransform: 'uppercase',
          }}
        >
          {`${userDetails == null ? 'Subscribe' : 'Upgrade'} to Unlock`}
        </Typography>
        <Typography
          sx={{
            color: theme.palette.text.primary,
            fontSize: '24px',
            textTransform: 'uppercase',
            display: 'block',
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            paddingBottom: '1em',
            paddingLeft: '1em',
            fontSize: '16px',
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontSize: 'inherit',
              textTransform: 'uppercase',
            }}
          >
            {subtitle}
          </Typography>
          <Stack spacing={2} sx={{ mt: 3 }}>
            {items.map((item, i) => (
              <ListItem
                key={i}
                sx={{
                  padding: 0,
                  display: 'flex',
                  alignItems: 'flex-start',
                }}
              >
                <ListItemIcon sx={{ minWidth: '36px' }}>
                  <CheckCircleIcon
                    color="primary"
                    sx={{
                      fontSize: 24,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={item}
                  sx={{
                    m: 0,
                    '& .MuiTypography-root': {
                      color: theme.palette.text.primary,
                      fontSize: '16px',
                      lineHeight: '24px',
                      fontWeight: 400,
                      textAlign: 'left',
                    },
                  }}
                />
              </ListItem>
            ))}
          </Stack>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '6px',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            sx={{
              textTransform: 'uppercase',
              backgroundColor: theme.palette.primary.main,
              // TODO: We need better default text color when on buttons.  All
              // modes should have roughly the below to be legible on our
              // SpotGamma green button
              color: 'black',
              fontSize: '14px',
              py: '6px',
              px: '24px',
            }}
            onClick={() =>
              window.open(
                'https://spotgamma.com/subscribe-to-spotgamma/',
                '_blank',
              )
            }
          >
            {`${userDetails == null ? 'Subscribe' : 'Upgrade'} Now`}
          </Button>
          {userDetails == null && (
            <Button
              sx={{
                textTransform: 'uppercase',
                backgroundColor: theme.palette.text.disabled,
                color: 'black',
                fontSize: '14px',
                py: '6px',
                px: '24px',
              }}
              onClick={() => navigate('/login')}
            >
              Login
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};
